import { CartLinesUpdateEvent } from '@shopify/events';

(() => {
  const KEY = 'viextaWishlist';
  let hydrationInFlight = null;

  const cleanPrice = (value) => {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = String(value ?? '');
    const decoded = decoder.value;
    const holder = document.createElement('div');
    holder.innerHTML = decoded;
    return (holder.textContent || decoded).replace(/\s+/g, ' ').trim();
  };

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(KEY) || '[]');
      if (!Array.isArray(value)) return [];
      return value.map((item) => ({ ...item, price: cleanPrice(item.price) }));
    } catch (_) {
      return [];
    }
  };

  const write = (items) => {
    localStorage.setItem(KEY, JSON.stringify(items));
    refresh();
  };

  const esc = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const getLabels = () => {
    const source = document.querySelector('[data-viexta-wishlist-open]');
    return {
      add: source?.dataset.viextaLabelAdd || 'Add to cart',
      choose: source?.dataset.viextaLabelChoose || 'Choose options',
      sold: source?.dataset.viextaLabelSold || 'Sold out',
      adding: source?.dataset.viextaLabelAdding || 'Adding...',
      added: source?.dataset.viextaLabelAdded || 'Added',
      error: source?.dataset.viextaLabelError || 'Could not add to cart'
    };
  };

  const refreshCount = (items) => {
    document.querySelectorAll('[data-viexta-wishlist-count]').forEach((badge) => {
      badge.textContent = items.length;
      badge.hidden = items.length === 0;
    });
  };

  const refreshButtons = (items) => {
    const handles = new Set(items.map((item) => item.handle));
    document.querySelectorAll('[data-viexta-wishlist-add]').forEach((button) => {
      const saved = handles.has(button.dataset.productHandle);
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      const label = button.querySelector('[data-viexta-wishlist-label]');
      if (label) label.textContent = saved ? 'Saved to wishlist' : 'Add to wishlist';

      const productTitle = button.dataset.productTitle || 'product';
      if (button.hasAttribute('data-viexta-wishlist-icon-only')) {
        button.setAttribute('aria-label', saved ? 'Remove ' + productTitle + ' from wishlist' : 'Add ' + productTitle + ' to wishlist');
        button.setAttribute('title', saved ? 'Remove from wishlist' : 'Add to wishlist');
      }
    });
  };

  const actionMarkup = (item, labels) => {
    if (item.available === false) {
      return '<button type="button" class="viexta-wishlist-item__action is-disabled" disabled>' + esc(labels.sold) + '</button>';
    }

    if (item.hasOnlyDefaultVariant === true && item.variantId) {
      return '<button type="button" class="viexta-wishlist-item__action" data-viexta-wishlist-cart data-variant-id="' + esc(item.variantId) + '">' + esc(labels.add) + '</button>';
    }

    return '<a class="viexta-wishlist-item__action viexta-wishlist-item__action--secondary" href="' + esc(item.url) + '">' + esc(labels.choose) + '</a>';
  };

  const refreshDrawer = (items) => {
    const labels = getLabels();

    document.querySelectorAll('[data-viexta-wishlist-items]').forEach((container) => {
      container.innerHTML = items.map((item) =>
        '<article class="viexta-wishlist-item">' +
          '<a class="viexta-wishlist-item__image-link" href="' + esc(item.url) + '">' +
            (item.image ? '<img class="viexta-wishlist-item__image" src="' + esc(item.image) + '" alt="' + esc(item.title) + '" loading="lazy">' : '') +
          '</a>' +
          '<div class="viexta-wishlist-item__info">' +
            '<p class="viexta-wishlist-item__title"><a class="viexta-wishlist-item__link" href="' + esc(item.url) + '">' + esc(item.title) + '</a></p>' +
            '<p class="viexta-wishlist-item__price">' + esc(cleanPrice(item.price)) + '</p>' +
            '<div class="viexta-wishlist-item__actions">' + actionMarkup(item, labels) + '</div>' +
          '</div>' +
          '<button type="button" class="button-unstyled viexta-wishlist-remove" data-viexta-wishlist-remove="' + esc(item.handle) + '" aria-label="Remove ' + esc(item.title) + ' from wishlist" title="Remove from wishlist">' +
            '<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">' +
              '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" fill="currentColor" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
          '</button>' +
        '</article>'
      ).join('');
    });

    document.querySelectorAll('[data-viexta-wishlist-empty]').forEach((empty) => {
      empty.hidden = items.length !== 0;
    });
  };

  const refresh = () => {
    const items = read();
    refreshCount(items);
    refreshButtons(items);
    refreshDrawer(items);
  };

  const needsHydration = (item) =>
    item.hasOnlyDefaultVariant === undefined ||
    item.available === undefined ||
    (item.hasOnlyDefaultVariant === true && !item.variantId);

  const hydrateItem = async (item) => {
    if (!needsHydration(item) || !item.handle) return item;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'products/' + encodeURIComponent(item.handle) + '.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) return item;

      const product = await response.json();
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const firstAvailable = variants.find((variant) => variant.available) || variants[0];

      return {
        ...item,
        variantId: firstAvailable?.id ? String(firstAvailable.id) : '',
        hasOnlyDefaultVariant: variants.length === 1,
        available: variants.length === 1 ? Boolean(firstAvailable?.available) : Boolean(product.available)
      };
    } catch (_) {
      return item;
    }
  };

  const hydrateLegacyItems = async () => {
    if (hydrationInFlight) return hydrationInFlight;

    const items = read();
    if (!items.some(needsHydration)) return null;

    hydrationInFlight = Promise.all(items.map(hydrateItem))
      .then((hydrated) => {
        localStorage.setItem(KEY, JSON.stringify(hydrated));
        refresh();
      })
      .finally(() => {
        hydrationInFlight = null;
      });

    return hydrationInFlight;
  };

  let lastTrigger = null;

  const openDrawer = () => {
    refresh();
    hydrateLegacyItems();
    document.querySelectorAll('[data-viexta-wishlist-drawer]').forEach((drawer) => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
    });
    document.querySelectorAll('[data-viexta-wishlist-overlay]').forEach((overlay) => {
      overlay.hidden = false;
    });
    document.documentElement.style.overflow = 'hidden';
    const drawer = document.querySelector('[data-viexta-wishlist-drawer]');
    drawer?.focus();
  };

  const closeDrawer = () => {
    document.querySelectorAll('[data-viexta-wishlist-drawer]').forEach((drawer) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    });
    document.querySelectorAll('[data-viexta-wishlist-overlay]').forEach((overlay) => {
      overlay.hidden = true;
    });
    document.documentElement.style.overflow = '';
    lastTrigger?.focus?.();
    lastTrigger = null;
  };

  const fetchCart = async () => {
    const response = await fetch(Theme.routes.cart_url + '.json', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Failed to refresh cart');
    return response.json();
  };

  const addToCart = async (button) => {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const labels = getLabels();
    const originalText = button.textContent;
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = new Set();

    cartItemsComponents.forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    let settled = false;

    button.disabled = true;
    button.textContent = labels.adding;

    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'dialog',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: Array.from(sectionIds).join(','),
          sections_url: window.location.pathname
        })
      });

      const payload = await response.json();
      const cart = await fetchCart();

      if (!response.ok || payload.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'viexta-wishlist',
            itemCount: 1,
            didError: true
          }
        });
        settled = true;
        throw new Error(payload.description || payload.message || labels.error);
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'viexta-wishlist',
          itemCount: 1,
          sections: payload.sections,
          didError: false
        }
      });
      settled = true;

      button.textContent = labels.added;
      closeDrawer();

      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer) {
        await customElements.whenDefined('theme-drawer');
        requestAnimationFrame(() => {
          if (!cartDrawer.hasAttribute('open')) cartDrawer.open?.();
        });
      }
    } catch (error) {
      if (!settled) {
        try {
          const cart = await fetchCart();
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'viexta-wishlist',
              itemCount: 1,
              didError: true
            }
          });
        } catch (cartError) {
          deferred.reject(cartError);
        }
      }

      button.textContent = labels.error;
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1800);
      console.warn('[viexta-wishlist] Add to cart failed', error);
    }
  };

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-viexta-wishlist-open]');
    if (open) {
      event.preventDefault();
      lastTrigger = open;
      openDrawer();
      return;
    }

    const close = event.target.closest('[data-viexta-wishlist-close], [data-viexta-wishlist-overlay]');
    if (close) {
      closeDrawer();
      return;
    }

    const cartButton = event.target.closest('[data-viexta-wishlist-cart]');
    if (cartButton) {
      event.preventDefault();
      addToCart(cartButton);
      return;
    }

    const add = event.target.closest('[data-viexta-wishlist-add]');
    if (add) {
      lastTrigger = add;
      const item = {
        handle: add.dataset.productHandle,
        title: add.dataset.productTitle,
        url: add.dataset.productUrl,
        image: add.dataset.productImage,
        price: cleanPrice(add.dataset.productPrice),
        variantId: add.dataset.productVariantId || '',
        hasOnlyDefaultVariant: add.dataset.productHasOnlyDefaultVariant === undefined
          ? undefined
          : add.dataset.productHasOnlyDefaultVariant === 'true',
        available: add.dataset.productAvailable === undefined
          ? undefined
          : add.dataset.productAvailable === 'true'
      };
      const items = read();
      const index = items.findIndex((existing) => existing.handle === item.handle);
      const isAdding = index < 0;
      if (index >= 0) items.splice(index, 1);
      else items.unshift(item);
      write(items);
      if (isAdding) openDrawer();
      return;
    }

    const remove = event.target.closest('[data-viexta-wishlist-remove]');
    if (remove) {
      write(read().filter((item) => item.handle !== remove.dataset.viextaWishlistRemove));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
  });

  window.addEventListener('storage', refresh);
  document.addEventListener('DOMContentLoaded', refresh);
  document.addEventListener('shopify:section:load', refresh);
  refresh();
})();